import { useTranslation } from "react-i18next";
import type { Route, ThemeClasses, RoutePoint } from "../types";
import { LINEAR_BUFFER_DEFAULT } from "../types";
import Modal from "../../../../../../components/base/Modal";
import { APIProvider } from "@vis.gl/react-google-maps";
import { RouteMapPreview } from "./RouteMapPreview";
import { getStatusHumanLabel } from "../../../../../../utils/getStatusEntity";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";

interface RouteDetailsModalProps {
  isOpen: boolean;
  route: Route | null;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onClose: () => void;
  onEdit?: (route: Route) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "text-primary-500 bg-primary-500/10";
    case "INACTIVE":
      return "text-red-500 bg-red-500/10";
    case "MAINTENANCE":
      return "text-yellow-500 bg-yellow-500/10";
    default:
      return "text-gray-500 bg-gray-500/10";
  }
};

const formatDuration = (minutes: number | undefined): string => {
  if (!minutes || minutes <= 0) return "-";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
};

const ROLE_META = {
  start:   { label: "Origen",  bg: "bg-emerald-500" },
  waypoint:{ label: "Vía",     bg: "bg-amber-400" },
  end:     { label: "Destino", bg: "bg-red-500" },
};

function getPointRole(index: number, total: number): "start" | "waypoint" | "end" {
  if (index === 0) return "start";
  if (index === total - 1) return "end";
  return "waypoint";
}

export function RouteDetailsModal({
  isOpen,
  route,
  themeClasses,
  isDarkMode,
  onClose,
  onEdit,
}: RouteDetailsModalProps) {
  const { t } = useTranslation("adminSections");
  const disableCRUD = useDisableCRUD();

  if (!isOpen || !route) return null;

  const routePoints: RoutePoint[] = (
    route.principal_route?.length >= 2
      ? route.principal_route
      : []
  ).map((p) => ({
    latitude: p.latitud,
    longitude: p.longitud,
    address: null,
    geocoding: false,
  }));

  const distance = route.principal_route_distance || 0;
  const duration = route.principal_route_duration;

  const inputClassName = `w-full text-xs 2xl:text-base px-3 py-2 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-100"
  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text}`;

  return (
    <Modal
      title={route.name}
      onClose={onClose}
      themeClasses={themeClasses}
      width="w-full sm:w-[80vw]"
      showEditButton={!!onEdit}
      onEdit={() => onEdit?.(route)}
      disableCRUD={disableCRUD}
      content={
        <APIProvider
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          libraries={["places"]}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-auto">
            {/* ── Info panel ─────────────────────────────────────────────── */}
            <div className="flex flex-col w-full gap-5 overflow-y-auto max-h-[75vh] pr-2">
              {/* Name */}
              <div className="flex flex-col gap-1.5">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  Nombre de la ruta
                </label>
                <div className={inputClassName}>{route.name}</div>
              </div>

              {/* Status */}
              <div className="flex flex-col gap-1.5">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  Estado
                </label>
                <div>
                  <span
                    className={`text-sm px-3 py-1.5 rounded-full ${getStatusColor(route.status.name)}`}
                  >
                    {getStatusHumanLabel(route.status.name, t)}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="flex flex-col gap-1.5">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  Descripción
                </label>
                <div className={`${inputClassName} min-h-[60px]`}>
                  {route.description || "Sin descripción"}
                </div>
              </div>

              <hr className={`${themeClasses.border}`} />

              {/* Route stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className={`p-3 rounded-lg ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`}>
                  <span className={`text-xs ${themeClasses.textSecondary}`}>Puntos</span>
                  <p className={`text-sm ${themeClasses.text} font-semibold`}>{routePoints.length}</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`}>
                  <span className={`text-xs ${themeClasses.textSecondary}`}>Distancia</span>
                  <p className={`text-sm ${themeClasses.text} font-semibold`}>{distance.toFixed(1)} km</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`}>
                  <span className={`text-xs ${themeClasses.textSecondary}`}>Duración</span>
                  <p className={`text-sm ${themeClasses.text} font-semibold`}>{formatDuration(duration)}</p>
                </div>
                <div className={`p-3 rounded-lg ${isDarkMode ? "bg-dark-card" : "bg-gray-100"}`}>
                  <span className={`text-xs ${themeClasses.textSecondary}`}>Corredor</span>
                  <p className={`text-sm ${themeClasses.text} font-semibold`}>
                    {(route.principal_route_buffer ?? LINEAR_BUFFER_DEFAULT) * 2} m
                  </p>
                </div>
              </div>

              {/* Points list with semantic roles */}
              <div className="flex flex-col gap-2">
                <label className={`text-sm font-medium ${themeClasses.text}`}>
                  Puntos de la ruta
                </label>
                <div className={`flex flex-col rounded-lg border ${themeClasses.cardBorder} overflow-hidden`}>
                  {routePoints.length === 0 ? (
                    <div className={`p-4 text-center ${themeClasses.textSecondary} text-sm`}>
                      No hay puntos definidos
                    </div>
                  ) : (
                    routePoints.map((point, index) => {
                      const role = getPointRole(index, routePoints.length);
                      const meta = ROLE_META[role];
                      return (
                        <div
                          key={index}
                          className={`flex items-center gap-3 px-3 py-2.5 ${
                            index !== routePoints.length - 1
                              ? `border-b ${themeClasses.cardBorder}`
                              : ""
                          } ${isDarkMode ? "bg-dark-card" : "bg-gray-50"}`}
                        >
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold text-white shrink-0 ${meta.bg}`}
                          >
                            {meta.label}
                          </span>
                          <div className="flex gap-4 flex-1">
                            <div>
                              <span className={`text-[10px] ${themeClasses.textSecondary}`}>Lat</span>
                              <p className={`text-xs ${themeClasses.text} font-mono`}>
                                {point.latitude.toFixed(6)}
                              </p>
                            </div>
                            <div>
                              <span className={`text-[10px] ${themeClasses.textSecondary}`}>Lng</span>
                              <p className={`text-xs ${themeClasses.text} font-mono`}>
                                {point.longitude.toFixed(6)}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className={`flex-1 text-xs 2xl:text-base py-2.5 px-4 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} font-medium transition-colors`}
                >
                  Cerrar
                </button>
                {onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(route)}
                    className="flex-1 text-xs 2xl:text-base py-2.5 px-4 bg-primary-500 hover:bg-primary-400 text-white rounded-lg font-medium transition-colors"
                  >
                    Editar Ruta
                  </button>
                )}
              </div>
            </div>

            {/* ── Map (read-only) ─────────────────────────────────────────── */}
            <div className="lg:w-full h-[75vh] max-h-[900px] rounded-xl overflow-hidden">
              <RouteMapPreview
                themeClasses={themeClasses}
                isDarkMode={isDarkMode}
                isEdit={false}
                routePoints={routePoints}
                routeDistance={distance}
                estimatedTime={formatDuration(duration)}
                firstPointRadius={route.first_point_radius ?? 0}
                lastPointRadius={route.last_point_radius ?? 0}
                corridorBuffer={route.principal_route_buffer ?? LINEAR_BUFFER_DEFAULT}
                storedAxis={route.principal_route_dense}
                onPointsChange={() => {}}
                onRouteCalculated={() => {}}
                readOnly={true}
              />
            </div>
          </div>
        </APIProvider>
      }
    />
  );
}
